import type {
  FinancialPlan,
  GoalInput,
  GoalTemplateParams,
  GoalType,
  PlanEventCategory,
} from '@/api/generated/model';
import { GoalType as GoalTypeEnum } from '@/api/generated/model';

/** Templates exposed in pickers. */
export const SAVINGS_GOAL_TEMPLATE_IDS = [
  GoalTypeEnum.emergency_fund,
  GoalTypeEnum.isa_allowance,
  GoalTypeEnum.tax_reserve,
  GoalTypeEnum.mortgage_payoff,
  GoalTypeEnum.savings,
] as const;

export type SavingsGoalTemplateId = (typeof SAVINGS_GOAL_TEMPLATE_IDS)[number];

export const SUCCESSION_GOAL_TEMPLATE_IDS = [
  GoalTypeEnum.fund_family_trust,
  GoalTypeEnum.annual_gift_allowance,
  GoalTypeEnum.generational_transfer,
] as const;

export type SuccessionGoalTemplateId = (typeof SUCCESSION_GOAL_TEMPLATE_IDS)[number];
export type GoalTemplateId = SavingsGoalTemplateId | SuccessionGoalTemplateId;

export interface GoalTemplateContext {
  currency: string;
  defaultAccountId?: string;
  plans: FinancialPlan[];
}

export interface GoalTemplatePreset {
  goalType: GoalTemplateId;
  nameKey: string;
  descriptionKey: string;
  category: PlanEventCategory;
  priority: 'mandatory' | 'nice-to-have';
  defaultDueDate: () => string;
  buildTemplateParams: (ctx: GoalTemplateContext) => GoalTemplateParams;
  resolveTargetAmount: (params: GoalTemplateParams, ctx: GoalTemplateContext) => number | undefined;
}

const currentTaxYear = (): number => {
  const now = new Date();
  return now.getMonth() >= 3 ? now.getFullYear() + 1 : now.getFullYear();
};

function resolveLinkedPlan(
  params: GoalTemplateParams | undefined,
  plans: FinancialPlan[]
): FinancialPlan | undefined {
  if (params?.linkedPlanId) {
    return plans.find((p) => p.id === params.linkedPlanId);
  }
  return plans.find((p) => p.isDefault) ?? plans[0];
}

/** Emergency fund target = plan monthlyLivingExpenses × months when plan is linked. */
export function computeEmergencyFundTarget(
  templateParams: GoalTemplateParams | undefined,
  plans: FinancialPlan[]
): number | undefined {
  const months = templateParams?.monthsOfExpenses ?? 6;
  const plan = resolveLinkedPlan(templateParams, plans);
  const monthly = plan?.assumptions?.monthlyLivingExpenses;
  if (monthly == null || monthly <= 0) return undefined;
  return monthly * months;
}

export const GOAL_TEMPLATE_IDS = [
  ...SAVINGS_GOAL_TEMPLATE_IDS,
  ...SUCCESSION_GOAL_TEMPLATE_IDS,
] as const;

export const GOAL_TEMPLATE_PRESETS: Record<GoalTemplateId, GoalTemplatePreset> = {
  [GoalTypeEnum.emergency_fund]: {
    goalType: GoalTypeEnum.emergency_fund,
    nameKey: 'goals.templates.emergency_fund.name',
    descriptionKey: 'goals.templates.emergency_fund.description',
    category: 'lifestyle',
    priority: 'mandatory',
    defaultDueDate: () => {
      const d = new Date();
      d.setFullYear(d.getFullYear() + 1);
      return d.toISOString().split('T')[0]!;
    },
    buildTemplateParams: (ctx) => {
      const plan = ctx.plans.find((p) => p.isDefault) ?? ctx.plans[0];
      return {
        monthsOfExpenses: 6,
        linkedPlanId: plan?.id,
      };
    },
    resolveTargetAmount: (params, ctx) => computeEmergencyFundTarget(params, ctx.plans),
  },
  [GoalTypeEnum.isa_allowance]: {
    goalType: GoalTypeEnum.isa_allowance,
    nameKey: 'goals.templates.isa_allowance.name',
    descriptionKey: 'goals.templates.isa_allowance.description',
    category: 'lifestyle',
    priority: 'mandatory',
    defaultDueDate: () => `${currentTaxYear()}-04-05`,
    buildTemplateParams: () => ({ taxYear: currentTaxYear() - 1 }),
    resolveTargetAmount: () => 20_000,
  },
  [GoalTypeEnum.tax_reserve]: {
    goalType: GoalTypeEnum.tax_reserve,
    nameKey: 'goals.templates.tax_reserve.name',
    descriptionKey: 'goals.templates.tax_reserve.description',
    category: 'borrowing',
    priority: 'mandatory',
    defaultDueDate: () => `${currentTaxYear()}-01-31`,
    buildTemplateParams: () => ({
      taxYear: currentTaxYear() - 1,
      estimatedTaxLiability: 15_000,
    }),
    resolveTargetAmount: (params) => params.estimatedTaxLiability,
  },
  [GoalTypeEnum.mortgage_payoff]: {
    goalType: GoalTypeEnum.mortgage_payoff,
    nameKey: 'goals.templates.mortgage_payoff.name',
    descriptionKey: 'goals.templates.mortgage_payoff.description',
    category: 'realEstate',
    priority: 'nice-to-have',
    defaultDueDate: () => {
      const d = new Date();
      d.setFullYear(d.getFullYear() + 10);
      return d.toISOString().split('T')[0]!;
    },
    buildTemplateParams: () => ({ remainingMortgageBalance: 150_000 }),
    resolveTargetAmount: (params) => params.remainingMortgageBalance,
  },
  [GoalTypeEnum.savings]: {
    goalType: GoalTypeEnum.savings,
    nameKey: 'goals.templates.savings.name',
    descriptionKey: 'goals.templates.savings.description',
    category: 'lifestyle',
    priority: 'nice-to-have',
    defaultDueDate: () => {
      const d = new Date();
      d.setMonth(d.getMonth() + 6);
      return d.toISOString().split('T')[0]!;
    },
    buildTemplateParams: () => ({}),
    resolveTargetAmount: () => 5_000,
  },
  [GoalTypeEnum.fund_family_trust]: {
    goalType: GoalTypeEnum.fund_family_trust,
    nameKey: 'goals.templates.fund_family_trust.name',
    descriptionKey: 'goals.templates.fund_family_trust.description',
    category: 'estate',
    priority: 'mandatory',
    defaultDueDate: () => {
      const d = new Date();
      d.setFullYear(d.getFullYear() + 3);
      return d.toISOString().split('T')[0]!;
    },
    buildTemplateParams: (ctx) => ({
      linkedPlanId: (ctx.plans.find((p) => p.isDefault) ?? ctx.plans[0])?.id,
      transferIntent: 'Fund family trust',
      beneficiaryAllocationPercent: 100,
    }),
    resolveTargetAmount: () => 50_000,
  },
  [GoalTypeEnum.annual_gift_allowance]: {
    goalType: GoalTypeEnum.annual_gift_allowance,
    nameKey: 'goals.templates.annual_gift_allowance.name',
    descriptionKey: 'goals.templates.annual_gift_allowance.description',
    category: 'estate',
    priority: 'nice-to-have',
    defaultDueDate: () => `${new Date().getFullYear()}-12-31`,
    buildTemplateParams: (ctx) => ({
      linkedPlanId: (ctx.plans.find((p) => p.isDefault) ?? ctx.plans[0])?.id,
      transferIntent: 'Use annual gift allowance',
      beneficiaryAllocationPercent: 100,
    }),
    resolveTargetAmount: () => 3_000,
  },
  [GoalTypeEnum.generational_transfer]: {
    goalType: GoalTypeEnum.generational_transfer,
    nameKey: 'goals.templates.generational_transfer.name',
    descriptionKey: 'goals.templates.generational_transfer.description',
    category: 'estate',
    priority: 'mandatory',
    defaultDueDate: () => {
      const d = new Date();
      d.setFullYear(d.getFullYear() + 10);
      return d.toISOString().split('T')[0]!;
    },
    buildTemplateParams: (ctx) => ({
      linkedPlanId: (ctx.plans.find((p) => p.isDefault) ?? ctx.plans[0])?.id,
      transferIntent: 'Generational transfer target',
      beneficiaryAllocationPercent: 100,
    }),
    resolveTargetAmount: () => 100_000,
  },
};

export function isSavingsGoalTemplateId(value: string): value is SavingsGoalTemplateId {
  return (SAVINGS_GOAL_TEMPLATE_IDS as readonly string[]).includes(value);
}

export function isGoalTemplateId(value: string): value is GoalTemplateId {
  return (GOAL_TEMPLATE_IDS as readonly string[]).includes(value);
}

export function buildGoalInputFromTemplate(
  templateId: GoalTemplateId,
  ctx: GoalTemplateContext,
  t: (key: string) => string
): GoalInput {
  const preset = GOAL_TEMPLATE_PRESETS[templateId];
  const templateParams = preset.buildTemplateParams(ctx);
  const targetAmount = preset.resolveTargetAmount(templateParams, ctx) ?? 0;

  return {
    name: t(preset.nameKey),
    description: t(preset.descriptionKey),
    accountId: ctx.defaultAccountId ?? '',
    targetAmount,
    currentAmount: 0,
    currency: ctx.currency,
    dueDate: preset.defaultDueDate(),
    priority: preset.priority,
    category: preset.category,
    active: true,
    goalType: preset.goalType as GoalType,
    templateParams,
  };
}

export function buildPlannedGoalFromTemplate(
  templateId: GoalTemplateId,
  ctx: GoalTemplateContext,
  t: (key: string) => string,
  rank: number
): {
  id: string;
  name: string;
  description?: string;
  targetAmount: number;
  currentAmount: number;
  currency: string;
  dueDate: string;
  priority: 'mandatory' | 'nice-to-have';
  rank: number;
  monthlyAllocation: number;
  yearlyAllocation: number;
  goalType: GoalType;
  templateParams?: GoalTemplateParams;
} {
  const input = buildGoalInputFromTemplate(templateId, ctx, t);
  return {
    id: crypto.randomUUID(),
    name: input.name,
    description: input.description,
    targetAmount: input.targetAmount,
    currentAmount: input.currentAmount ?? 0,
    currency: input.currency,
    dueDate: input.dueDate ?? presetDueIso(templateId),
    priority: (input.priority as 'mandatory' | 'nice-to-have') ?? 'nice-to-have',
    rank,
    monthlyAllocation: 0,
    yearlyAllocation: 0,
    goalType: input.goalType!,
    templateParams: input.templateParams,
  };
}

function presetDueIso(templateId: GoalTemplateId): string {
  return GOAL_TEMPLATE_PRESETS[templateId].defaultDueDate();
}
